import React, { useState, useEffect } from 'react';

const TommyLoadingAnimation = ({ message = "Generating...", showProgress = true, progress = null }) => {
  const [position, setPosition] = useState(0);
  const [butterflyPos, setButterflyPos] = useState(80);
  const [frame, setFrame] = useState(0);

  // Use actual progress if provided, otherwise animate
  const displayProgress = progress !== null ? progress : Math.min(position * 1.2, 95);

  // Animate Tommy chasing butterfly
  useEffect(() => {
    const interval = setInterval(() => {
      setPosition(prev => {
        const newPos = prev + 2;
        if (newPos > 85) return 0;
        return newPos;
      });
      setButterflyPos(prev => {
        const newPos = prev + 3;
        if (newPos > 100) return 15;
        return newPos;
      });
      setFrame(prev => (prev + 1) % 4);
    }, 150);
    return () => clearInterval(interval);
  }, []);

  // 8-bit Tommy frames (running cat)
  const tommyFrames = [
    // Frame 1 - legs back
    `
    ╱|、
   (˚ˎ 。7
    |、˜〵
    じしˍ,)ノ
    `,
    // Frame 2 - mid stride
    `
    ╱|、
   (˚ˎ 。7
    |、˜〵
    じしˍ,)ノ~
    `,
    // Frame 3 - legs forward
    `
     ╱|、
   (˚ˎ 。7
    |、˜〵
   ~じしˍ,)ノ
    `,
    // Frame 4 - jump
    `
      ╱|、
    (˚ˎ 。7
     |、˜〵
     じしˍ,)ノ
    `
  ];

  return (
    <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center">
      <div className="bg-zinc-900 border-4 border-teal-500 rounded-lg p-8 max-w-md w-full mx-4 shadow-[0_0_30px_rgba(20,184,166,0.3)]">
        {/* Retro header */}
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-teal-400 font-mono tracking-wider animate-pulse">
            ⟨ LOADING ⟩
          </h2>
          <p className="text-zinc-400 text-sm mt-1 font-mono">{message}</p>
        </div>

        {/* Animation area */}
        <div className="relative h-24 bg-zinc-950 border-2 border-zinc-700 rounded overflow-hidden mb-4">
          {/* Grass floor */}
          <div className="absolute bottom-0 left-0 right-0 h-4 bg-gradient-to-t from-green-900 to-green-800 border-t-2 border-green-600">
            <div className="flex justify-around text-green-500 text-xs">
              {[...Array(20)].map((_, i) => (
                <span key={i} style={{ opacity: Math.random() * 0.5 + 0.5 }}>▲</span>
              ))}
            </div>
          </div>

          {/* Butterfly */}
          <div 
            className="absolute text-2xl transition-all duration-150"
            style={{ 
              left: `${butterflyPos}%`, 
              top: `${20 + Math.sin(Date.now() / 200) * 10}px`,
              transform: 'scaleX(-1)'
            }}
          >
            🦋
          </div>

          {/* Tommy the cat */}
          <div 
            className="absolute bottom-4 transition-all duration-150"
            style={{ left: `${position}%` }}
          >
            <pre className="text-xs text-orange-400 font-mono leading-none" style={{ fontSize: '8px' }}>
              {tommyFrames[frame]}
            </pre>
          </div>

          {/* Pixel dust particles */}
          {[...Array(5)].map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-teal-400/50 rounded-full animate-ping"
              style={{
                left: `${(position - 5 - i * 3)}%`,
                bottom: `${16 + Math.random() * 8}px`,
                animationDelay: `${i * 0.1}s`
              }}
            />
          ))}
        </div>

        {/* 8-bit progress bar */}
        {showProgress && (
          <div className="mb-4">
            <div className="h-6 bg-zinc-950 border-2 border-zinc-600 rounded overflow-hidden relative">
              {/* Animated fill */}
              <div 
                className="h-full bg-gradient-to-r from-teal-600 via-teal-400 to-teal-600 transition-all duration-300"
                style={{ 
                  width: `${displayProgress}%`,
                  backgroundSize: '20px 20px',
                  animation: 'shimmer 1s linear infinite'
                }}
              />
              {/* Pixel blocks overlay */}
              <div className="absolute inset-0 flex items-center justify-start px-1 gap-1">
                {[...Array(Math.floor(displayProgress / 5))].map((_, i) => (
                  <div key={i} className="w-3 h-4 bg-teal-300 border border-teal-500" />
                ))}
              </div>
            </div>
            <p className="text-center text-zinc-500 text-xs font-mono mt-1">
              {progress !== null ? `${Math.round(displayProgress)}% complete` : 'Tommy is working on it...'}
            </p>
          </div>
        )}

        {/* Retro tips */}
        <div className="border-t-2 border-dashed border-zinc-700 pt-4">
          <p className="text-zinc-500 text-xs font-mono text-center animate-pulse">
            💡 TIP: Videos take 2-5 minutes to generate
          </p>
        </div>

        {/* Scanlines effect */}
        <div 
          className="absolute inset-0 pointer-events-none opacity-10"
          style={{
            background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.3) 2px, rgba(0,0,0,0.3) 4px)'
          }}
        />
      </div>

      <style jsx>{`
        @keyframes shimmer {
          0% { background-position: -20px 0; }
          100% { background-position: 20px 0; }
        }
      `}</style>
    </div>
  );
};

export default TommyLoadingAnimation;
